<template>
  <div class="global-ai-assistant" :class="{ 'hidden': isHidden }">
    <!-- 浮動按鈕 -->
    <div 
      v-if="!isExpanded" 
      class="ai-float-button"
      @click="toggleAssistant"
      :class="{ 'has-notification': hasNewMessage }"
    >
      <div class="ai-icon">🤖</div>
      <div class="ai-label">AI助手</div>
      <div v-if="hasNewMessage" class="notification-dot"></div>
    </div>

    <!-- 展開的對話窗口 -->
    <div 
      v-if="isExpanded" 
      class="ai-chat-window"
      :class="{ 'minimized': isMinimized }"
    >
      <!-- 標題列 -->
      <div class="chat-header">
        <div class="header-left">
          <div class="ai-avatar">🤖</div>
          <div class="header-info">
            <div class="header-title">AI智能助手</div>
            <div class="header-context">{{ currentContext }}</div>
          </div>
        </div>
        <div class="header-actions">
          <a-button 
            type="text" 
            size="small" 
            @click="clearMessages"
            class="clear-btn"
            :disabled="chatHistory.length === 0"
          >
            🗑️
          </a-button>
          <a-button 
            type="text" 
            size="small" 
            @click="toggleMinimize"
            class="minimize-btn"
          >
            {{ isMinimized ? '📈' : '📉' }}
          </a-button>
          <a-button 
            type="text" 
            size="small" 
            @click="closeAssistant"
            class="close-btn"
          >
            ✕
          </a-button>
        </div>
      </div>

      <!-- 對話內容區域 -->
      <div v-if="!isMinimized" class="chat-content">
        <!-- 對話歷史 -->
        <div class="chat-messages" ref="messagesContainer">
          <div 
            v-for="message in chatHistory" 
            :key="message.id"
            class="message-item"
            :class="{ 'user-message': message.role === 'user', 'ai-message': message.role === 'assistant' }"
          >
            <div class="message-avatar">
              {{ message.role === 'user' ? '👤' : '🤖' }}
            </div>
            <div class="message-content">
              <div class="message-text">{{ message.content }}</div>
              <div class="message-time">{{ formatTime(message.timestamp) }}</div>
              
              <!-- AI回應的操作按鈕 -->
              <div v-if="message.role === 'assistant' && message.actions" class="message-actions">
                <a-button 
                  v-for="action in message.actions" 
                  :key="action.type"
                  type="primary" 
                  size="small"
                  @click="handleAction(action)"
                >
                  {{ action.label }}
                </a-button>
              </div>
            </div>
          </div>
          
          <!-- 歡迎訊息 -->
          <div v-if="chatHistory.length === 0" class="welcome-message">
            <div class="welcome-content">
              <div class="welcome-icon">🤖</div>
              <h4>{{ contextualWelcome.title }}</h4>
              <p>{{ contextualWelcome.description }}</p>
              <div class="quick-actions">
                <a-button 
                  v-for="suggestion in contextualWelcome.suggestions" 
                  :key="suggestion"
                  size="small"
                  @click="applySuggestion(suggestion)"
                  class="suggestion-btn"
                >
                  {{ suggestion }}
                </a-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 輸入區域 -->
        <div class="chat-input">
          <a-textarea
            v-model:value="userInput"
            :rows="2"
            :maxlength="500"
            placeholder="輸入您的需求... (Ctrl + Enter 發送)"
            @keydown.enter.ctrl.exact="sendMessage"
            @keydown.enter.exact.prevent="newLine"
            :disabled="isProcessing"
            class="input-field"
          />
          <div class="input-actions">
            <a-button
              type="primary"
              @click="sendMessage"
              :loading="isProcessing"
              :disabled="!userInput.trim()"
              size="small"
            >
              發送
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiGeminiScheduleAssistant } from "@/utils/api/apiGeminiAI.js";
import { apiCalendarList } from "@/utils/api/apiAdminCalendar.js";
import dayjs from "dayjs";

export default {
  name: 'GlobalAIAssistant',
  data() {
    return {
      isExpanded: false,
      isMinimized: false,
      hasNewMessage: false,
      chatHistory: [],
      userInput: '',
      isProcessing: false,
      currentRoute: null,
      isHidden: false, // 當有 Modal 開啟時隱藏
    };
  },
  computed: {
    currentContext() {
      const route = this.$route;
      if (route.path.includes('/admin/calendar')) {
        return '📅 行事曆管理';
      } else if (route.path.includes('/admin/billing')) {
        return '💳 帳務管理';
      } else if (route.path.includes('/admin')) {
        return '⚙️ 系統管理';
      }
      return '🏠 系統總覽';
    },
    
    contextualWelcome() {
      const route = this.$route;
      if (route.path.includes('/admin/calendar')) {
        return {
          title: 'AI 時段助手',
          description: '我可以協助您快速設定預約時段、分析現有排程，並提供最佳化建議。',
          suggestions: [
            '新增本週平日 10:00-21:00 時段',
            '設定假日特殊營業時間',
            '分析目前時段使用狀況'
          ]
        };
      } else if (route.path.includes('/admin/billing')) {
        return {
          title: 'AI 帳務助手',
          description: '我可以協助您分析帳務資料、產生報表，並提供營收優化建議。',
          suggestions: [
            '分析本月營收狀況',
            '產生週營收報表',
            '查詢特定時段收益',
            '預測下月營收趨勢'
          ]
        };
      }
      return {
        title: 'AI 智能助手',
        description: '我可以協助您處理系統相關問題，提供操作指引和數據分析。',
        suggestions: [
          '系統操作說明',
          '數據分析報告',
          '功能使用指南',
          '問題解決建議'
        ]
      };
    }
  },
  watch: {
    '$route'(to, from) {
      this.currentRoute = to.path;
      // 路由切換時清空對話歷史
      if (to.path !== from.path) {
        this.chatHistory = [];
      }
    }
  },
  mounted() {
    this.currentRoute = this.$route.path;
    console.log('GlobalAIAssistant mounted on route:', this.$route.path);
    console.log('GlobalAIAssistant component is visible');
    
    // 監聽 Modal 狀態
    this.setupModalListener();
  },
  
  beforeUnmount() {
    // 清理定時器
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    
    // 清理 resize 超時
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // 清理事件監聽器
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  },
  methods: {
    toggleAssistant() {
      this.isExpanded = !this.isExpanded;
      this.hasNewMessage = false;
      if (this.isExpanded) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    
    closeAssistant() {
      this.isExpanded = false;
      this.isMinimized = false;
    },
    
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
    },
    
    clearMessages() {
      this.chatHistory = [];
      this.$message.success('對話記錄已清空');
    },
    
    async sendMessage() {
      if (!this.userInput.trim() || this.isProcessing) return;
      
      const userMessage = {
        id: Date.now(),
        role: 'user',
        content: this.userInput,
        timestamp: new Date()
      };
      
      this.chatHistory.push(userMessage);
      const userPrompt = this.userInput;
      this.userInput = '';
      this.isProcessing = true;
      
      try {
        // 根據當前路由決定上下文
        const context = await this.buildContextData();
        
        const response = await apiGeminiScheduleAssistant({
          prompt: userPrompt,
          context: context,
          currentPage: this.currentRoute,
          chatHistory: this.chatHistory  // 傳送完整對話歷史
        });
        
        const aiResponseData = response.data;
        const actualAiData = aiResponseData.data || aiResponseData.datas || aiResponseData;
        
        const aiMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: actualAiData.message || aiResponseData.message,
          timestamp: new Date(),
          actions: this.generateActions(actualAiData)
        };
        
        this.chatHistory.push(aiMessage);
        
        // 如果窗口是最小化狀態，顯示新訊息提示
        if (this.isMinimized || !this.isExpanded) {
          this.hasNewMessage = true;
        }
        
      } catch (error) {
        console.error('AI 處理錯誤:', error);
        const errorMessage = error.response?.data?.message || 'AI 服務暫時無法使用，請稍後再試。';
        
        this.chatHistory.push({
          id: Date.now() + 1,
          role: 'assistant',
          content: errorMessage,
          timestamp: new Date(),
          isError: true
        });
      } finally {
        this.isProcessing = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    
    newLine() {
      this.userInput += '\n';
    },
    
    applySuggestion(suggestion) {
      this.userInput = suggestion;
      this.sendMessage();
    },
    
    async buildContextData() {
      const context = {};
      
      if (this.currentRoute.includes('/admin/calendar')) {
        // 如果是在日曆頁面，嘗試獲取當前頁面的資料
        try {
          // 優先使用全域方法獲取批次資料
          if (window.getCurrentBatchItems && typeof window.getCurrentBatchItems === 'function') {
            const currentBatchItems = window.getCurrentBatchItems();
            context.existingBatchItems = currentBatchItems;
            console.log('從全域方法獲取批次資料:', currentBatchItems);
          }
          
          // 嘗試從全域變數獲取
          if (window.currentBatchItems && Array.isArray(window.currentBatchItems)) {
            context.existingBatchItems = context.existingBatchItems || [...window.currentBatchItems];
            console.log('從全域變數獲取批次資料:', window.currentBatchItems);
          }
          
          // 嘗試從組件獲取其他資料
          const calendarComponent = this.getCalendarComponent();
          if (calendarComponent) {
            context.currentCalendarData = calendarComponent.calendarItems || [];
            context.currentMonth = calendarComponent.currentMonth;
            // 如果前面沒有獲取到批次資料，嘗試從組件獲取
            if (!context.existingBatchItems) {
              context.existingBatchItems = calendarComponent.batchItems || [];
            }
          }
          
          // 獲取當月完整時段資料用於AI分析
          await this.loadCurrentMonthDataForAnalysis(context);
          
          // 確保有批次資料的描述
          if (context.existingBatchItems && context.existingBatchItems.length > 0) {
            context.batchSummary = `目前批次新增中有 ${context.existingBatchItems.length} 筆時段設定`;
            context.hasExistingBatch = true;
          } else {
            context.hasExistingBatch = false;
          }
          
        } catch (error) {
          console.log('無法獲取日曆上下文資料:', error);
        }
      }
      
      return context;
    },
    
    getCalendarComponent() {
      console.log('getCalendarComponent called, currentRoute:', this.currentRoute);
      
      // 方法1: 檢查全域註冊的日曆組件 (最可靠的方法)
      if (window.adminCalendarComponent) {
        console.log('找到日曆組件 (全域註冊):', window.adminCalendarComponent.$options.name, 'openBatchAdd:', typeof window.adminCalendarComponent.openBatchAdd);
        return window.adminCalendarComponent;
      }
      
      // 方法2: 向上搜尋父組件 (備用方法)
      let parent = this.$parent;
      let level = 0;
      while (parent && level < 10) { // 防止無限循環
        console.log(`Searching parent level ${level}:`, parent.$options.name || 'unnamed');
        
        if (parent.$options.name === 'AdminCalendarView' || 
            (parent.calendarItems !== undefined || parent.batchItems !== undefined)) {
          console.log('找到日曆組件 (父級搜尋):', parent.$options.name, 'openBatchAdd:', typeof parent.openBatchAdd);
          return parent;
        }
        parent = parent.$parent;
        level++;
      }
      
      // 方法3: 檢查當前路由是否為日曆頁面，使用全域查找 (最後手段)
      if (this.currentRoute.includes('/admin/calendar')) {
        console.log('Route includes /admin/calendar, trying global search');
        
        // 嘗試通過 Vue devtools 或全域查找
        const app = this.$root;
        const findCalendarComponent = (vnode, depth = 0) => {
          if (!vnode || depth > 10) return null; // 防止過深搜索
          
          console.log(`Searching depth ${depth}:`, vnode.$options?.name || 'unnamed');
          
          // 檢查當前 vnode
          if (vnode.$options && vnode.$options.name === 'AdminCalendarView') {
            console.log('Found AdminCalendarView at depth', depth);
            return vnode;
          }
          if (vnode.calendarItems !== undefined || vnode.batchItems !== undefined) {
            console.log('Found calendar-like component at depth', depth, vnode.$options?.name);
            return vnode;
          }
          
          // 遞歸搜尋子組件
          if (vnode.$children) {
            for (let child of vnode.$children) {
              const result = findCalendarComponent(child, depth + 1);
              if (result) return result;
            }
          }
          return null;
        };
        
        const calendarComponent = findCalendarComponent(app);
        if (calendarComponent) {
          console.log('找到日曆組件 (全域搜尋):', calendarComponent.$options.name, 'openBatchAdd:', typeof calendarComponent.openBatchAdd);
          return calendarComponent;
        }
      }
      
      console.log('未找到日曆組件, current route:', this.currentRoute);
      return null;
    },
    
    generateActions(aiData) {
      const actions = [];
      
      if (aiData.scheduleData && Array.isArray(aiData.scheduleData)) {
        actions.push({
          type: 'apply_schedule',
          label: '套用時段設定',
          data: aiData.scheduleData
        });
      }
      
      if (this.currentRoute.includes('/admin/calendar')) {
        actions.push({
          type: 'open_batch_add',
          label: '開啟批次新增'
        });
      }
      
      return actions;
    },
    
    handleAction(action) {
      switch (action.type) {
        case 'apply_schedule':
          this.applyScheduleData(action.data);
          break;
        case 'open_batch_add':
          this.openBatchAdd();
          break;
      }
    },
    
    applyScheduleData(scheduleData) {
      // 檢查當前路由是否為日曆頁面
      if (!this.currentRoute.includes('/admin/calendar')) {
        this.$message.warning('請在行事曆頁面使用此功能');
        return;
      }

      // 先開啟批次新增視窗，然後套用資料
      this.openBatchAddWithData(scheduleData);
    },
    
    openBatchAdd() {
      console.log('openBatchAdd called, currentRoute:', this.currentRoute);
      
      // 首先檢查當前路由是否為日曆頁面
      if (!this.currentRoute.includes('/admin/calendar')) {
        console.log('Not on calendar page, currentRoute:', this.currentRoute);
        this.$message.warning('請前往行事曆頁面使用此功能');
        return;
      }

      const calendarComponent = this.getCalendarComponent();
      console.log('getCalendarComponent result:', calendarComponent);
      console.log('openBatchAdd method exists:', calendarComponent && typeof calendarComponent.openBatchAdd === 'function');
      
      if (calendarComponent && calendarComponent.openBatchAdd) {
        calendarComponent.openBatchAdd();
      } else {
        // 更詳細的錯誤處理
        console.error('Calendar component not found or openBatchAdd method missing');
        console.error('Current route:', this.currentRoute);
        console.error('Calendar component:', calendarComponent);
        this.$message.warning('請前往行事曆頁面使用此功能');
      }
    },

    openBatchAddWithData(scheduleData) {
      // 先獲取目前批次新增的現有內容
      const currentBatchData = this.getCurrentBatchData();
      
      // 將現有內容和 AI 建議一起傳送
      const dataWithContext = {
        aiSuggestions: scheduleData,
        currentBatchItems: currentBatchData
      };
      
      // 使用全域事件匯流排（如果存在）
      if (window.eventBus && window.eventBus.emit) {
        window.eventBus.emit('apply-ai-schedule', dataWithContext);
      }
      
      // 使用全域處理器陣列直接調用
      if (window.aiScheduleHandlers && window.aiScheduleHandlers.length > 0) {
        window.aiScheduleHandlers.forEach(handler => {
          try {
            handler(dataWithContext);
          } catch (error) {
            console.error('呼叫 AI 時段處理器時發生錯誤:', error);
          }
        });
      }
      
      if (currentBatchData && currentBatchData.length > 0) {
        this.$message.success(`AI 建議已送出，已包含目前 ${currentBatchData.length} 筆現有設定，正在開啟批次新增...`);
      } else {
        this.$message.success('AI 建議已送出，正在開啟批次新增...');
      }
    },
    
    // 獲取目前批次新增的現有內容
    getCurrentBatchData() {
      // 嘗試從日曆組件獲取當前的 batchItems
      if (window.getCurrentBatchItems && typeof window.getCurrentBatchItems === 'function') {
        return window.getCurrentBatchItems();
      }
      
      // 作為備用方案，從全域變數獲取（需要日曆組件設定）
      if (window.currentBatchItems && Array.isArray(window.currentBatchItems)) {
        return [...window.currentBatchItems];
      }
      
      console.log('無法獲取目前批次新增內容，將僅傳送 AI 建議');
      return [];
    },
    
    formatTime(timestamp) {
      return dayjs(timestamp).format('HH:mm');
    },
    
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
      }
    },
    
    // 載入當月資料用於AI分析
    async loadCurrentMonthDataForAnalysis(context) {
      try {
        console.log('正在載入當月資料用於AI分析...');
        
        // 獲取當前月份
        const currentMonth = dayjs().format('YYYY-MM-DD');
        console.log('請求月份資料:', currentMonth);
        
        // 呼叫日曆API獲取當月資料
        const response = await apiCalendarList({ month: currentMonth });
        console.log('API回應:', response.data);
        
        if (response.data && response.data.datas) {
          const monthData = response.data.datas;
          console.log(`成功載入當月資料: ${monthData.length} 個時段`);
          
          // 添加完整的月份資料到context
          context.currentMonthData = monthData;
          
          // 計算統計資料
          const holidaySlots = monthData.filter(slot => slot.type === 'holiday').length;
          const normalSlots = monthData.filter(slot => slot.type === 'normal').length;
          const pastSlots = monthData.filter(slot => {
            const slotDate = dayjs(slot.bookingAvailableStartTime);
            return slotDate.isBefore(dayjs(), 'day');
          }).length;
          const futureSlots = monthData.length - pastSlots;
          
          // 添加統計資料
          context.monthlyStats = {
            totalSlots: monthData.length,
            holidaySlots,
            normalSlots,
            pastSlots,
            futureSlots,
            currentMonth: dayjs().format('YYYY年MM月')
          };
          
          console.log('月份統計:', context.monthlyStats);
        } else {
          console.log('當月無時段資料');
          context.monthlyStats = {
            totalSlots: 0,
            holidaySlots: 0,
            normalSlots: 0,
            pastSlots: 0,
            futureSlots: 0,
            currentMonth: dayjs().format('YYYY年MM月')
          };
        }
      } catch (error) {
        console.log('載入當月資料失敗:', error);
        // 即使失敗也提供基本統計結構
        context.monthlyStats = {
          totalSlots: 0,
          holidaySlots: 0,
          normalSlots: 0,
          pastSlots: 0,
          futureSlots: 0,
          currentMonth: dayjs().format('YYYY年MM月'),
          loadError: true
        };
      }
    },

    // 設置 Modal 監聽器
    setupModalListener() {
      // 檢查是否為手機版
      const isMobile = () => window.innerWidth <= 768;
      
      // 檢查 Modal 狀態的函數
      const checkModalStatus = () => {
        // 桌面版永遠不隱藏 AI 助手
        if (!isMobile()) {
          if (this.isHidden) {
            this.isHidden = false;
            console.log('AI Assistant always visible on desktop');
          }
          return;
        }
        
        // 手機版才執行隱藏邏輯
        // 檢查多種可能的 Modal 元素
        const modals = document.querySelectorAll(
          '.ant-modal-mask, .ant-drawer-mask, .ant-modal-wrap, .ant-drawer-wrapper'
        );
        
        // 檢查是否有可見的 Modal
        let hasVisibleModal = false;
        modals.forEach(modal => {
          const computedStyle = window.getComputedStyle(modal);
          if (computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden') {
            hasVisibleModal = true;
          }
        });
        
        // 也檢查 body 的 overflow 狀態（Modal 開啟時通常會設置）
        const hasScrollLock = document.body.style.overflow === 'hidden' || 
                             document.body.classList.contains('ant-scrolling-effect');
        
        const shouldHide = hasVisibleModal || hasScrollLock;
        
        if (this.isHidden !== shouldHide) {
          this.isHidden = shouldHide;
          console.log('AI Assistant visibility changed (mobile):', shouldHide ? 'hidden' : 'visible');
        }
      };
      
      // 立即檢查一次
      checkModalStatus();
      
      // 監聽 DOM 變化
      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            // 檢查是否有 Modal 相關元素被添加或移除
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1 && (
                node.classList?.contains('ant-modal-mask') ||
                node.classList?.contains('ant-modal-wrap') ||
                node.classList?.contains('ant-drawer-mask') ||
                node.classList?.contains('ant-drawer-wrapper') ||
                node.querySelector?.('.ant-modal-mask, .ant-drawer-mask, .ant-modal-wrap, .ant-drawer-wrapper')
              )) {
                shouldCheck = true;
              }
            });
            
            mutation.removedNodes.forEach(node => {
              if (node.nodeType === 1 && (
                node.classList?.contains('ant-modal-mask') ||
                node.classList?.contains('ant-modal-wrap') ||
                node.classList?.contains('ant-drawer-mask') ||
                node.classList?.contains('ant-drawer-wrapper') ||
                node.querySelector?.('.ant-modal-mask, .ant-drawer-mask, .ant-modal-wrap, .ant-drawer-wrapper')
              )) {
                shouldCheck = true;
              }
            });
          }
          
          // 監聽屬性變化（如 display, visibility）
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
            const target = mutation.target;
            if (target.classList?.contains('ant-modal-mask') ||
                target.classList?.contains('ant-modal-wrap') ||
                target.classList?.contains('ant-drawer-mask') ||
                target.classList?.contains('ant-drawer-wrapper')) {
              shouldCheck = true;
            }
          }
        });
        
        if (shouldCheck) {
          // 延遲一點檢查，確保 DOM 完全更新
          setTimeout(checkModalStatus, 50);
        }
      });
      
      // 監聽整個 document，確保能捕捉到所有變化
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
      
      // 也監聽 body 的 style 變化
      const bodyObserver = new MutationObserver(checkModalStatus);
      bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
      
      // 定期檢查（作為備用方案）
      this.checkInterval = setInterval(checkModalStatus, 1000);
      
      // 監聽窗口大小變化
      const handleResize = () => {
        // 延遲執行，避免頻繁觸發
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(checkModalStatus, 100);
      };
      
      window.addEventListener('resize', handleResize);
      
      // 存儲事件處理器，以便清理
      this.resizeHandler = handleResize;
    }
  }
};
</script>

<style scoped>
.global-ai-assistant {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.global-ai-assistant.hidden {
  opacity: 0;
  transform: scale(0.8);
  pointer-events: none;
}

/* 浮動按鈕樣式 */
.ai-float-button {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  position: relative;
}

/* 手機版響應式調整 */
@media (max-width: 768px) {
  .global-ai-assistant {
    bottom: 10px;
    right: 10px;
  }
  
  .ai-float-button {
    width: 50px;
    height: 50px;
    box-shadow: 0 2px 12px rgba(102, 126, 234, 0.3);
  }
  
  .ai-icon {
    font-size: 16px !important;
    margin-bottom: 0 !important;
  }
  
  .ai-label {
    font-size: 8px !important;
    font-weight: 400;
  }
  
  .notification-dot {
    top: 4px;
    right: 4px;
    width: 8px;
    height: 8px;
    border: 1px solid white;
  }
  
  /* 手機版對話窗口調整 */
  .ai-chat-window {
    width: calc(100vw - 20px);
    max-width: 300px;
    height: 400px;
  }
  
  .ai-chat-window.minimized {
    height: 40px;
  }
  
  .chat-header {
    padding: 8px 12px;
  }
  
  .header-title {
    font-size: 12px;
  }
  
  .header-context {
    font-size: 10px;
  }
  
  .chat-messages {
    padding: 12px;
    gap: 8px;
  }
  
  .message-text {
    font-size: 12px;
    padding: 6px 10px;
  }
  
  .chat-input {
    padding: 8px;
  }
  
  .input-field {
    font-size: 12px;
  }
}

.ai-float-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(102, 126, 234, 0.6);
}

.ai-float-button.has-notification {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.ai-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

.ai-label {
  font-size: 10px;
  font-weight: 500;
}

.notification-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 12px;
  height: 12px;
  background: #ff4d4f;
  border-radius: 50%;
  border: 2px solid white;
}

/* 對話窗口樣式 */
.ai-chat-window {
  width: 350px;
  height: 500px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
}

.ai-chat-window.minimized {
  height: 50px;
}

.chat-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
}

.ai-avatar {
  font-size: 18px;
  margin-right: 8px;
}

.header-info {
  flex: 1;
}

.header-title {
  font-weight: 600;
  font-size: 14px;
}

.header-context {
  font-size: 11px;
  opacity: 0.8;
}

.header-actions {
  display: flex;
  gap: 4px;
}

.clear-btn, .minimize-btn, .close-btn {
  color: white !important;
  border: none !important;
  background: transparent !important;
}

.clear-btn:hover, .minimize-btn:hover, .close-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.clear-btn:disabled {
  color: rgba(255, 255, 255, 0.5) !important;
  cursor: not-allowed;
}

.clear-btn:disabled:hover {
  background: transparent !important;
}

.chat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-item {
  display: flex;
  gap: 8px;
}

.user-message {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.user-message .message-avatar {
  background: #1890ff;
  color: white;
}

.ai-message .message-avatar {
  background: #f0f0f0;
}

.message-content {
  flex: 1;
  max-width: calc(100% - 36px);
}

.user-message .message-content {
  text-align: right;
}

.message-text {
  background: #f0f0f0;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
}

.user-message .message-text {
  background: #1890ff;
  color: white;
}

.message-time {
  font-size: 10px;
  color: #999;
  margin-top: 4px;
}

.message-actions {
  margin-top: 8px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.welcome-message {
  text-align: center;
  color: #666;
}

.welcome-content {
  max-width: 280px;
  margin: 0 auto;
}

.welcome-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.welcome-content h4 {
  color: #333;
  margin-bottom: 8px;
  font-size: 16px;
}

.welcome-content p {
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 16px;
}

.quick-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.suggestion-btn {
  font-size: 11px;
  height: auto;
  padding: 6px 12px;
}

.chat-input {
  border-top: 1px solid #f0f0f0;
  padding: 12px;
  background: #fafafa;
}

.input-field {
  resize: none;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 13px;
}

.input-actions {
  margin-top: 8px;
  text-align: right;
}

/* 滾動條樣式 */
.chat-messages::-webkit-scrollbar {
  width: 4px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>